import { useEffect, useState } from 'react';
import { checkFirebaseAuthHealth } from '@/config/authHealthCheck';
import { t } from '../i18n/t';

/**
 * Renders nothing when healthy. Block 49 — see authHealthCheck.ts's own
 * header for why this exists: a dead Firebase Auth key produced no visible
 * signal anywhere for five weeks, only a generic per-attempt SDK error that
 * never named the actual cause. This runs once, on app load, regardless of
 * route (mounted unconditionally in App.tsx, above routing) — the whole
 * point is that it shows even ON the sign-in page, before anyone has tried
 * to sign in and hit the generic error themselves.
 */
export default function ConfigHealthBanner() {
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    // BLOCK 51 FIX — this check hits the real production Identity Toolkit
    // endpoint unconditionally. In emulator mode (VITE_USE_EMULATOR=true,
    // same flag firebase.ts already gates on), VITE_FIREBASE_API_KEY is
    // frontend/.env.emulator's deliberate placeholder ("emulator-unused") —
    // Vite's own mode-file precedence means that value ALWAYS wins over
    // whatever real key sits in frontend/.env, so this check was guaranteed
    // to report API_KEY_INVALID every time regardless of the real key's
    // actual health. The Auth SDK itself is correctly on the local emulator
    // in this mode (see the connectAuthEmulator call below) — production
    // key state is simply irrelevant here, so skip the check entirely.
    if (import.meta.env.VITE_USE_EMULATOR === 'true') return;

    let cancelled = false;
    checkFirebaseAuthHealth(import.meta.env.VITE_FIREBASE_API_KEY).then(result => {
      if (!cancelled && !result.healthy) setReason(result.reason || t('common:configHealthBanner.unknownFirebaseAuthMisconfiguration'));
    });
    return () => { cancelled = true; };
  }, []);

  if (!reason) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: '#b91c1c', color: 'white',
        padding: '10px 16px', fontSize: '14px', fontFamily: 'monospace',
        textAlign: 'center',
      }}
    >{t('common:configHealthBanner.firebaseAuthIsMisconfiguredSignIn')}{reason})
    </div>
  );
}
